import React, { useState } from 'react';
import { format, parseISO } from 'date-fns';
import { useTheme } from '../../context/ThemeContext';
import {
  Expense,
  EXPENSE_STATUSES,
  EXPENSE_CATEGORIES,
  ExpenseStatus,
} from '../../types/expense';
import { useTranslation } from 'react-i18next';
import AddButton from '../../components/common/AddButton';
import {
  EyeIcon,
  EditIcon,
  TrashIcon,
  SendIcon,
  ReceiptIcon,
  PlusIcon,
} from '../icons';

interface ExpenseListProps {
  expenses: Expense[];
  loading: boolean;
  onEdit: (expense: Expense) => void;
  onDelete: (id: number) => void;
  onSubmit: (id: number) => void;
  onViewDetail: (expense: Expense) => void;
  onAdd: () => void;
}

// Icons
const Icons = {
  eye: <EyeIcon className="w-4 h-4" strokeWidth={1.5} />,
  edit: <EditIcon className="w-4 h-4" strokeWidth={1.5} />,
  trash: <TrashIcon className="w-4 h-4" strokeWidth={1.5} />,
  send: <SendIcon className="w-4 h-4" strokeWidth={1.5} />,
  receipt: <ReceiptIcon className="w-8 h-8" strokeWidth={1.5} />,
  plus: <PlusIcon className="w-4 h-4" strokeWidth={2} />,
};

function getStatusConfig(status: ExpenseStatus) {
  return (
    EXPENSE_STATUSES.find((s) => s.value === status) ?? EXPENSE_STATUSES[0]
  );
}

function getCategoryLabel(category: string): string {
  return (
    EXPENSE_CATEGORIES.find((c) => c.value === category)?.label ?? category
  );
}

function formatCurrency(amount: string, currency = 'CAD'): string {
  const num = parseFloat(amount) || 0;
  return new Intl.NumberFormat('en-CA', {
    style: 'currency',
    currency,
    minimumFractionDigits: 2,
  }).format(num);
}

function StatusBadge({
  status,
  isDark,
}: {
  status: ExpenseStatus;
  isDark: boolean;
}) {
  const badgeStyles: Record<ExpenseStatus, string> = {
    draft: isDark
      ? 'bg-slate-500/10 text-slate-400 border border-slate-500/20'
      : 'bg-slate-100 text-slate-600 border border-slate-200',
    pending: isDark
      ? 'bg-amber-500/10 text-amber-400 border border-amber-500/20'
      : 'bg-amber-50 text-amber-700 border border-amber-200',
    approved: isDark
      ? 'bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'
      : 'bg-emerald-50 text-emerald-700 border border-emerald-200',
    rejected: isDark
      ? 'bg-rose-500/10 text-rose-400 border border-rose-500/20'
      : 'bg-rose-50 text-rose-700 border border-rose-200',
    reimbursed: isDark
      ? 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'
      : 'bg-cyan-50 text-cyan-700 border border-cyan-200',
  };

  const label = getStatusConfig(status).label;

  return (
    <span
      className={`inline-flex items-center px-2.5 py-1 rounded-lg text-xs font-medium ${badgeStyles[status]}`}
    >
      {label}
    </span>
  );
}

// Skeleton loading row
function SkeletonRow({ isDark }: { isDark: boolean }) {
  const shimmer = isDark ? 'bg-slate-700/50' : 'bg-slate-200';
  return (
    <tr>
      {[...Array(6)].map((_, i) => (
        <td key={i} className="px-5 py-4">
          <div className={`h-4 rounded ${shimmer} animate-pulse`} />
        </td>
      ))}
    </tr>
  );
}

// Skeleton loading card for mobile
function SkeletonCard({ isDark }: { isDark: boolean }) {
  const shimmer = isDark ? 'bg-slate-700/50' : 'bg-slate-200';
  return (
    <div
      className={`rounded-xl p-4 ${isDark ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <div className={`h-4 w-24 rounded ${shimmer} animate-pulse`} />
          <div className={`h-3 w-32 rounded ${shimmer} animate-pulse`} />
          <div className={`h-3 w-20 rounded ${shimmer} animate-pulse`} />
        </div>
        <div className={`h-6 w-16 rounded ${shimmer} animate-pulse`} />
      </div>
    </div>
  );
}

const canMutate = (status: ExpenseStatus) =>
  status === 'draft' || status === 'pending';
const canSubmit = (status: ExpenseStatus) => status === 'draft';

const ExpenseList: React.FC<ExpenseListProps> = ({
  expenses,
  loading,
  onEdit,
  onDelete,
  onSubmit,
  onViewDetail,
  onAdd,
}) => {
  const { isDark } = useTheme();

  const thClass = `px-5 py-3 text-left text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`;
  const tdClass = `px-5 py-4 whitespace-nowrap text-sm ${isDark ? 'text-slate-300' : 'text-slate-700'}`;
  const { t } = useTranslation();
  // Empty state
  if (!loading && expenses.length === 0) {
    return (
      <div
        className={`p-12 text-center ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
      >
        <div
          className={`w-16 h-16 mx-auto mb-4 rounded-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-slate-100'}`}
        >
          <span className={isDark ? 'text-slate-500' : 'text-slate-400'}>
            {Icons.receipt}
          </span>
        </div>
        <p
          className={`text-base font-medium mb-1 ${isDark ? 'text-slate-300' : 'text-slate-600'}`}
        >
          No expenses yet
        </p>
        <p
          className={`text-sm mb-4 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
        >
          Track your business expenses by adding your first one.
        </p>
        <AddButton
          label={t('expense.addExpense')}
          onClick={onAdd}
        />
      </div>
    );
  }

  return (
    <>
      {/* Mobile Cards */}
      <div className="md:hidden px-4 pb-4 space-y-3">
        {loading
          ? [...Array(4)].map((_, i) => (
              <SkeletonCard key={i} isDark={isDark} />
            ))
          : expenses.map((expense) => (
              <div
                key={expense.id}
                className={`rounded-xl p-4 ${isDark ? 'bg-slate-800/50 border border-slate-700/50' : 'bg-white border border-slate-200 shadow-sm'}`}
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <span
                        className={`text-sm font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {format(parseISO(expense.expense_date), 'dd MMM yyyy')}
                      </span>
                    </div>
                    <div
                      className={`font-medium truncate ${isDark ? 'text-slate-200' : 'text-slate-700'}`}
                    >
                      {expense.merchant_name || '—'}
                    </div>
                    <div
                      className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                    >
                      {getCategoryLabel(expense.category)}
                    </div>
                    {expense.description && (
                      <p
                        className={`text-sm mt-1.5 line-clamp-2 ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
                      >
                        {expense.description}
                      </p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-right">
                    <div
                      className={`text-base font-bold ${isDark ? 'text-white' : 'text-slate-900'}`}
                    >
                      {formatCurrency(expense.amount, expense.currency)}
                    </div>
                  </div>
                </div>

                {/* Badges */}
                <div className="flex flex-wrap items-center gap-2 mt-3">
                  <StatusBadge status={expense.status} isDark={isDark} />
                  {expense.billable && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-violet-500/10 text-violet-400 border border-violet-500/20' : 'bg-violet-50 text-violet-700 border border-violet-200'}`}
                    >
                      Billable
                    </span>
                  )}
                  {expense.reimbursable && (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded-md text-xs font-medium ${isDark ? 'bg-blue-500/10 text-blue-400 border border-blue-500/20' : 'bg-blue-50 text-blue-700 border border-blue-200'}`}
                    >
                      Reimbursable
                    </span>
                  )}
                </div>

                {/* Actions */}
                <div
                  className={`flex items-center justify-end gap-1 mt-3 pt-3 border-t ${isDark ? 'border-slate-700/50' : 'border-slate-100'}`}
                >
                  <button
                    onClick={() => onViewDetail(expense)}
                    className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                    title="View details"
                  >
                    {Icons.eye}
                  </button>
                  {canMutate(expense.status) && (
                    <button
                      onClick={() => onEdit(expense)}
                      className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                      title="Edit"
                    >
                      {Icons.edit}
                    </button>
                  )}
                  {canSubmit(expense.status) && (
                    <button
                      onClick={() => onSubmit(expense.id)}
                      className="p-2 rounded-lg text-violet-500 hover:bg-violet-500/10 transition-all"
                      title="Submit for approval"
                    >
                      {Icons.send}
                    </button>
                  )}
                  {canMutate(expense.status) && (
                    <button
                      onClick={() => onDelete(expense.id)}
                      className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                      title="Delete"
                    >
                      {Icons.trash}
                    </button>
                  )}
                </div>
              </div>
            ))}
      </div>

      {/* Desktop Table */}
      <div className="hidden md:block overflow-x-auto">
        <table className="min-w-full">
          <thead
            className={`sticky top-0 z-10 ${isDark ? 'bg-slate-900' : 'bg-white'}`}
          >
            <tr
              className={
                isDark
                  ? 'border-b border-slate-700/50'
                  : 'border-b border-slate-200'
              }
            >
              <th className={thClass}>Date</th>
              <th className={thClass}>Merchant</th>
              <th className={thClass}>Amount</th>
              <th className={thClass}>Category</th>
              <th className={thClass}>Status</th>
              <th
                className={`px-5 py-3 text-right text-xs font-semibold uppercase tracking-wider ${isDark ? 'text-slate-400' : 'text-slate-500'}`}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody
            className={
              isDark
                ? 'divide-y divide-slate-700/50'
                : 'divide-y divide-slate-100'
            }
          >
            {loading
              ? [...Array(5)].map((_, i) => (
                  <SkeletonRow key={i} isDark={isDark} />
                ))
              : expenses.map((expense, idx) => (
                  <tr
                    key={expense.id}
                    className={`transition-colors ${
                      isDark
                        ? idx % 2 === 0
                          ? 'bg-slate-900/10 hover:bg-slate-900/50'
                          : 'bg-slate-800/20 hover:bg-slate-900/50'
                        : idx % 2 === 0
                          ? 'bg-white hover:bg-slate-50'
                          : 'bg-slate-50/60 hover:bg-slate-50'
                    }`}
                  >
                    {/* Date */}
                    <td className={tdClass}>
                      <span
                        className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {format(parseISO(expense.expense_date), 'MMM dd, yyyy')}
                      </span>
                    </td>

                    {/* Merchant */}
                    <td className={tdClass}>
                      <div
                        className={`font-medium ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {expense.merchant_name || '—'}
                      </div>
                      {expense.description && (
                        <div
                          className={`text-xs mt-0.5 truncate max-w-[200px] ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                          title={expense.description}
                        >
                          {expense.description}
                        </div>
                      )}
                    </td>

                    {/* Amount */}
                    <td className={tdClass}>
                      <span
                        className={`font-semibold ${isDark ? 'text-white' : 'text-slate-900'}`}
                      >
                        {formatCurrency(expense.amount, expense.currency)}
                      </span>
                      <div className="flex gap-1 mt-1">
                        {expense.billable && (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-violet-500/10 text-violet-400' : 'bg-violet-50 text-violet-700'}`}
                          >
                            Billable
                          </span>
                        )}
                        {expense.reimbursable && (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded text-xs font-medium ${isDark ? 'bg-blue-500/10 text-blue-400' : 'bg-blue-50 text-blue-700'}`}
                          >
                            Reimb.
                          </span>
                        )}
                      </div>
                    </td>

                    {/* Category */}
                    <td className={tdClass}>
                      {getCategoryLabel(expense.category)}
                      {expense.project && (
                        <div
                          className={`text-xs mt-0.5 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        >
                          {expense.project.name}
                        </div>
                      )}
                    </td>

                    {/* Status */}
                    <td className="px-5 py-4 whitespace-nowrap">
                      <StatusBadge status={expense.status} isDark={isDark} />
                      {expense.approved_by && (
                        <div
                          className={`text-xs mt-1 ${isDark ? 'text-slate-500' : 'text-slate-400'}`}
                        >
                          by {expense.approved_by.full_name}
                        </div>
                      )}
                    </td>

                    {/* Actions */}
                    <td className="px-5 py-4 whitespace-nowrap text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => onViewDetail(expense)}
                          className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                          title="View details"
                        >
                          {Icons.eye}
                        </button>
                        {canMutate(expense.status) && (
                          <button
                            onClick={() => onEdit(expense)}
                            className={`p-2 rounded-lg transition-all ${isDark ? 'text-slate-400 hover:text-white hover:bg-slate-700' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-100'}`}
                            title="Edit"
                          >
                            {Icons.edit}
                          </button>
                        )}
                        {canSubmit(expense.status) && (
                          <button
                            onClick={() => onSubmit(expense.id)}
                            className="p-2 rounded-lg text-violet-500 hover:bg-violet-500/10 transition-all"
                            title="Submit for approval"
                          >
                            {Icons.send}
                          </button>
                        )}
                        {canMutate(expense.status) && (
                          <button
                            onClick={() => onDelete(expense.id)}
                            className="p-2 rounded-lg text-rose-500 hover:bg-rose-500/10 transition-all"
                            title="Delete"
                          >
                            {Icons.trash}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
          </tbody>
        </table>
      </div>
    </>
  );
};

export default ExpenseList;
